import { useTranslation } from 'react-i18next';

import styles from './PlanOptions.module.scss';
import { formatMoney, type StructuredPlanBreakdown } from './pricing';

export type PlanTotalSummaryProps = {
  readonly breakdown: StructuredPlanBreakdown;
  readonly currency: string;
};

/** Live preview matching server `pricing_breakdown` semantics. */
export function PlanTotalSummary({ breakdown, currency }: PlanTotalSummaryProps) {
  const { t } = useTranslation();
  return (
    <div className={styles.summary} aria-label={t('planOptions.pricingPreview')}>
      <div className={styles.summaryRow}>
        <span>{t('planOptions.summaryAligners', { qty: breakdown.manufacturedThisStage })}</span>
        <span className={styles.summaryValue}>{formatMoney(breakdown.alignersTotal, currency)}</span>
      </div>
      {breakdown.additionalPaidTotal > 0 && (
        <div className={styles.summaryRow}>
          <span>{t('planOptions.summaryExtras')}</span>
          <span className={styles.summaryValue}>
            {formatMoney(breakdown.additionalPaidTotal, currency)}
          </span>
        </div>
      )}
      {breakdown.discountsTotal > 0 && (
        <div className={styles.summaryRow}>
          <span>{t('planOptions.summaryDiscount')}</span>
          <span className={`${styles.summaryValue} ${styles.summaryValueNegative}`}>
            −{formatMoney(breakdown.discountsTotal, currency)}
          </span>
        </div>
      )}
      {breakdown.deposit > 0 && (
        <div className={styles.summaryRow}>
          <span>{t('planOptions.summaryDeposit')}</span>
          <span className={`${styles.summaryValue} ${styles.summaryValueNegative}`}>
            −{formatMoney(breakdown.deposit, currency)}
          </span>
        </div>
      )}
      <div className={styles.summaryTotal}>
        <span>{t('planOptions.pricingTotal')}</span>
        <span data-testid="plan-total">{formatMoney(breakdown.amountDue, currency)}</span>
      </div>
    </div>
  );
}
